import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Ticker from "@/components/site/Ticker";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { toast } from "sonner";

export default function SponsorPage() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("week");
  const [form, setForm] = useState({ brand_name: "", brand_url: "", tagline: "", email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sponsor/packages").then((r) => setPackages(r.data));
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand_name || !form.brand_url || !form.tagline || !form.email) {
      toast.error("Please fill every field");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/sponsor/checkout", {
        package_id: selected,
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Checkout failed", { description: err?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="sponsor-page" className="bg-white text-[#0a0a0a]">
      <Seo
        title="Sponsor Universtrend — own a slot in front of curious readers"
        description="Reach the world's most curious audience. Sponsor the homepage of Universtrend from $99/week. Self-serve Stripe checkout, instant activation, 0 effort."
      />
      <Header />
      <Ticker />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="overline text-[#52525b] mb-4">Self-serve sponsor</div>
            <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl lg:text-7xl">
              One tasteful slot.<br /><span className="text-[#ff3b30]">Yours, instantly.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base lg:text-lg text-[#52525b]">
              Sponsor the homepage of Universtrend. We only sell one slot at a time — your brand, your tagline, your link, in front of every reader. Stripe checkout, no contracts, live within a minute.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 mt-10 border-t border-l border-black">
              {packages.map((p) => {
                const isSel = selected === p.id;
                return (
                  <button
                    key={p.id}
                    data-testid={`sponsor-pkg-${p.id}`}
                    type="button"
                    onClick={() => setSelected(p.id)}
                    className={`border-r border-b border-black p-6 text-left transition-colors ${
                      isSel ? "bg-black text-white" : "bg-white hover:bg-[#fafafa]"
                    }`}
                  >
                    <div className="overline opacity-70">{p.label}</div>
                    <div className="mt-3 font-display font-extrabold text-4xl tracking-tighter">
                      ${p.amount.toFixed(0)}
                    </div>
                    <div className="mt-1 font-mono text-xs opacity-70">
                      ${(p.amount / p.days).toFixed(2)} / day
                    </div>
                    {isSel && <div className="mt-4 overline">Selected ✓</div>}
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} className="col-span-12 lg:col-span-5 border border-black p-6 lg:p-8 self-start" data-testid="sponsor-form">
            <div className="overline text-[#52525b] mb-4">Your slot</div>
            <label className="overline block mt-3 mb-1">Brand name</label>
            <input
              data-testid="sponsor-brand-name"
              value={form.brand_name}
              onChange={onChange("brand_name")}
              required
              maxLength={60}
              placeholder="Nike"
              className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
            />
            <label className="overline block mt-4 mb-1">Tagline (shown on homepage)</label>
            <input
              data-testid="sponsor-tagline"
              value={form.tagline}
              onChange={onChange("tagline")}
              required
              maxLength={80}
              placeholder="Just Search It."
              className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
            />
            <label className="overline block mt-4 mb-1">Link URL</label>
            <input
              data-testid="sponsor-brand-url"
              type="url"
              value={form.brand_url}
              onChange={onChange("brand_url")}
              required
              placeholder="https://nike.com"
              className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
            />
            <label className="overline block mt-4 mb-1">Email (receipt)</label>
            <input
              data-testid="sponsor-email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              required
              placeholder="ads@nike.com"
              className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
            />

            <button
              data-testid="sponsor-checkout-button"
              type="submit"
              disabled={busy}
              className="mt-6 w-full bg-black text-white py-4 overline hover:bg-[#ff3b30] transition-colors disabled:opacity-50"
            >
              {busy ? "Redirecting to Stripe…" : "Continue to checkout →"}
            </button>
            <p className="font-mono text-[10px] text-[#52525b] mt-3">
              Secure payment via Stripe. Test mode active — use card 4242 4242 4242 4242.
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
